import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../hooks/useAuthContext";
import authAPI from "../../services/auth";
import MeaButton from "../../components/MeaButton";
import MeaLink from "../../components/MeaLink";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuthenticated } = useAuthContext();
  
  const status = searchParams.get("status");
  const provider = searchParams.get("provider");
  const error = searchParams.get("error");
  const errorEmail = searchParams.get("email");

  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [customEmail, setCustomEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toastRef = useRef(null);

  const mockUsers = [
    { label: "Bassem Debbabi (SuperAdmin) - bassem@debbabi.net", value: "bassem@debbabi.net" },
    { label: "Karim Abdi (SuperAdmin) - kabdi213@gmail.com", value: "kabdi213@gmail.com" },
  ];

  useEffect(() => {
    if (status === "success") {
      checkAuthenticated()
        .then(() => {
          navigate("/school", { replace: true });
        })
        .catch((err) => {
          console.error("Auth check failed", err);
          setLoading(false);
        });
    } else if (status === "mock") {
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [status, checkAuthenticated, navigate]);

  const handleMockLoginSubmit = (e) => {
    e.preventDefault();
    const emailToUse = customEmail || selectedEmail;
    if (!emailToUse) {
      toastRef.current.show({
        severity: "warn",
        summary: "Email requis",
        detail: "Veuillez choisir ou saisir un email.",
        life: 5000,
      });
      return;
    }

    setSubmitting(true);
    authAPI.oauthMockLogin(emailToUse)
      .then(() => {
        return checkAuthenticated();
      })
      .then(() => {
        navigate("/school", { replace: true });
      })
      .catch((err) => {
        toastRef.current.show({
          severity: "error",
          summary: "Erreur",
          detail: err.message || "La connexion mock a échoué.",
          life: 5000,
        });
        setSubmitting(false);
      });
  };

  if (loading) {
    return (
      <div className="flex flex-column align-items-center justify-content-center h-screen">
        <ProgressSpinner />
        <p className="mt-3 text-700 font-medium">Authentification en cours...</p>
      </div>
    );
  }

  if (status === "mock") {
    return (
      <div className="flex align-items-start justify-content-center h-screen p-4" style={{
        backgroundImage: "url('/images/image-background.svg')",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        paddingTop: "100px"
      }}>
        <Toast ref={toastRef} />
        <Card title="SSO Mock Login (Développement)" style={{ maxWidth: "450px", width: "100%" }}>
          <p className="text-600 text-sm mb-4">
            Vous avez initié une connexion SSO via <strong>{provider}</strong> en mode local/développement. Choisissez un compte pour simuler la connexion :
          </p>

          <form onSubmit={handleMockLoginSubmit}>
            <label className="block text-900 font-medium mb-2">Sélectionner un compte système</label>
            <Dropdown
              value={selectedEmail}
              options={mockUsers}
              onChange={(e) => {
                setSelectedEmail(e.value);
                setCustomEmail("");
              }}
              placeholder="Sélectionnez un utilisateur"
              className="w-full mb-3"
            />

            <div className="text-center text-500 my-2">ou</div>

            <label className="block text-900 font-medium mb-2">Saisir n'importe quel email existant</label>
            <InputText
              value={customEmail}
              onChange={(e) => {
                setCustomEmail(e.target.value);
                setSelectedEmail("");
              }}
              placeholder="exemple@mon-ecole-arabe.com"
              className="w-full mb-4"
            />

            <MeaButton
              type="submit"
              label="Simuler la connexion SSO"
              icon="pi pi-sign-in"
              className="w-full"
              loading={submitting}
            />
          </form>

          <div className="text-center mt-4">
            <MeaLink to="/auth/login" className="text-sm text-blue-500">Retour à la connexion</MeaLink>
          </div>
        </Card>
      </div>
    );
  }

  // Error state
  let errorMessage = "Une erreur d'authentification est survenue.";
  if (error === "account_not_found") {
    errorMessage = `Aucun compte n'est associé à l'adresse e-mail "${errorEmail}". Veuillez utiliser l'adresse e-mail enregistrée dans votre école ou contacter votre administrateur.`;
  } else if (error === "oauth_disabled") {
    errorMessage = "La connexion SSO/OAuth est désactivée sur la plateforme.";
  } else if (error === "account_blocked") {
    errorMessage = "Ce compte a été bloqué par l'administrateur.";
  }

  return (
    <div className="flex align-items-start justify-content-center h-screen p-4" style={{
      backgroundImage: "url('/images/image-background.svg')",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      paddingTop: "100px"
    }}>
      <Card title="Erreur d'authentification" style={{ maxWidth: "450px", width: "100%" }} className="border-red-500 border-1 shadow-4">
        <div className="flex align-items-center gap-3 mb-4">
          <i className="pi pi-exclamation-triangle text-red-500" style={{ fontSize: "2rem" }}></i>
          <span className="text-900 font-medium">Échec de la connexion SSO</span>
        </div>
        
        <p className="text-700 line-height-3 mb-4">{errorMessage}</p>

        <div className="text-center">
          <MeaLink to="/auth/login">
            <MeaButton label="Retourner à la page de connexion" icon="pi pi-arrow-left" className="p-button-secondary w-full" />
          </MeaLink>
        </div>
      </Card>
    </div>
  );
}
